import _snakeCase from 'lodash/snakeCase';

export const getLessonAssetFolder = (
  lessonTitle: string,
  lessonId: string,
): string => `lessons/${_snakeCase(lessonTitle)}-${lessonId}`;

export const getCanonicalLessonAssetPath = (
  lessonTitle: string,
  lessonId: string,
  fileName: string,
): string => `/s3/${getLessonAssetFolder(lessonTitle, lessonId)}/${fileName}`;

export const isCanonicalLessonAssetPath = (
  path: string,
  lessonId: string,
): boolean => {
  const escapedLessonId = lessonId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  return new RegExp(`/s3/lessons/[^/]+-${escapedLessonId}/`).test(path);
};

export const isLegacyFlatLessonAssetPath = (
  path: string,
  lessonId: string,
): boolean =>
  path.includes(`/s3/lessons/${lessonId}/`) &&
  !isCanonicalLessonAssetPath(path, lessonId);

export const isTaskPreviewPath = (path: string): boolean =>
  typeof path === 'string' && path !== '' && /\/tasks\//.test(path);

export const isResultPreviewPath = (path: string): boolean =>
  typeof path === 'string' &&
  path !== '' &&
  path.endsWith('/result-preview.jpg');

export const getLessonUploadRequestParams = ({
  courseId,
  lessonId,
}: {
  courseId?: string;
  lessonId: string;
}) => ({
  isCourseRequest: Boolean(courseId),
  isLessonRequest: !courseId,
  requestParams: courseId ? { courseId, lessonId } : { lessonId },
});

const hasPreviewValue = (preview: unknown) =>
  typeof preview === 'string' && preview !== '';

const preferCanonicalPreview = ({
  apiPreview,
  basePreview,
  lessonId,
}: {
  apiPreview: unknown;
  basePreview: unknown;
  lessonId: string;
}) => {
  if (
    hasPreviewValue(apiPreview) &&
    hasPreviewValue(basePreview) &&
    isLegacyFlatLessonAssetPath(apiPreview as string, lessonId) &&
    isCanonicalLessonAssetPath(basePreview as string, lessonId)
  ) {
    return basePreview;
  }

  if (!hasPreviewValue(apiPreview) && hasPreviewValue(basePreview)) {
    return basePreview;
  }

  return apiPreview;
};

const resolveMergedResultPreview = ({
  apiResultPreview,
  baseResultPreview,
  lessonId,
}: {
  apiResultPreview: unknown;
  baseResultPreview: unknown;
  lessonId: string;
}) => {
  if (
    hasPreviewValue(apiResultPreview) &&
    isResultPreviewPath(apiResultPreview as string)
  ) {
    return preferCanonicalPreview({
      apiPreview: apiResultPreview,
      basePreview: baseResultPreview,
      lessonId,
    });
  }

  if (
    hasPreviewValue(apiResultPreview) &&
    isTaskPreviewPath(apiResultPreview as string)
  ) {
    return hasPreviewValue(baseResultPreview) ? baseResultPreview : undefined;
  }

  if (hasPreviewValue(baseResultPreview)) {
    return baseResultPreview;
  }

  return apiResultPreview;
};

export const mergeFigmaLessonPreviewsFromBase = (
  baseLesson: Record<string, any>,
  apiData: Record<string, any>,
) => {
  const lessonId = baseLesson?.id as string;
  const baseTasks = Array.isArray(baseLesson?.tasks) ? baseLesson.tasks : [];
  const apiTasks = Array.isArray(apiData?.tasks) ? apiData.tasks : [];

  const mergedTasks = apiTasks.map((task: Record<string, any>, index: number) => {
    const baseTask = baseTasks[index];
    const preview = preferCanonicalPreview({
      apiPreview: task?.preview,
      basePreview: baseTask?.preview,
      lessonId,
    });

    if (preview === task?.preview) {
      return task;
    }

    return {
      ...task,
      preview,
    };
  });

  let mergedInitialState = apiData.initialState;

  if (apiData.initialState && baseLesson?.initialState) {
    const preview = preferCanonicalPreview({
      apiPreview: apiData.initialState.preview,
      basePreview: baseLesson.initialState.preview,
      lessonId,
    });

    if (preview !== apiData.initialState.preview) {
      mergedInitialState = {
        ...apiData.initialState,
        preview,
      };
    }
  }

  const resultPreview = resolveMergedResultPreview({
    apiResultPreview: apiData.resultPreview,
    baseResultPreview: baseLesson?.resultPreview,
    lessonId,
  });

  return {
    ...apiData,
    tasks: mergedTasks,
    initialState: mergedInitialState,
    resultPreview,
  };
};
